import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface MaintenanceStatus {
  isActive: boolean;
  isWarning: boolean;
  message: string;
  endsAt: string | null;
  warningStartsAt: string | null;
}

export const useMaintenanceMode = () => {
  const [status, setStatus] = useState<MaintenanceStatus>({
    isActive: false,
    isWarning: false,
    message: '',
    endsAt: null,
    warningStartsAt: null
  });
  const [loading, setLoading] = useState(true);

  const checkMaintenanceStatus = async () => {
    try {
      // Don't use .single() - it causes 406 errors when no rows match
      const { data, error } = await supabase
        .from('site_maintenance')
        .select('*')
        .eq('is_active', true)
        .order('id', { ascending: false })
        .limit(1);

      if (error) {
        // Silently fail - don't block the app if maintenance table has issues
        console.warn('⚠️ Could not check maintenance status (table may not be accessible via REST API):', error);
        setStatus({
          isActive: false,
          isWarning: false,
          message: '',
          endsAt: null,
          warningStartsAt: null
        });
        setLoading(false);
        return;
      }

      // Check if we got any results
      if (!data || data.length === 0) {
        setStatus({
          isActive: false,
          isWarning: false,
          message: '',
          endsAt: null,
          warningStartsAt: null
        });
        return;
      }

      // Get the first (and only) result
      const maintenanceRecord = data[0];

      const now = new Date();
      const startsAt = maintenanceRecord.started_at ? new Date(maintenanceRecord.started_at) : null;
      const endsAt = maintenanceRecord.ends_at ? new Date(maintenanceRecord.ends_at) : null;

      // Check if we're in warning period (before maintenance starts)
      const isWarning = startsAt && now < startsAt;

      // Check if maintenance is active (after start time, before end time)
      const isActive = startsAt && endsAt && now >= startsAt && now < endsAt;

      // If maintenance period has ended, treat as inactive (read-only check).
      // Server-side cron handles the actual database deactivation.
      if (endsAt && now >= endsAt) {
        setStatus({
          isActive: false,
          isWarning: false,
          message: '',
          endsAt: null,
          warningStartsAt: null
        });
        return;
      }

      setStatus({
        isActive: isActive || false,
        isWarning: isWarning || false,
        message: maintenanceRecord.message || 'System maintenance in progress',
        endsAt: maintenanceRecord.ends_at,
        warningStartsAt: maintenanceRecord.started_at
      });

    } catch (error) {
      console.error('Error in checkMaintenanceStatus:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkMaintenanceStatus();

    // Check every 10 seconds
    const interval = setInterval(checkMaintenanceStatus, 10000);

    return () => clearInterval(interval);
  }, []);

  return { status, loading, refresh: checkMaintenanceStatus };
};

